// components/MyStorefront.js
import React, { useState, useRef, useEffect } from 'react';

export default function MyStorefront({ email }) {
  const [shopEnabled, setShopEnabled] = useState(false);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [shopName, setShopName] = useState('');
  const [hasBanner, setHasBanner] = useState(false);
  const [shippingFrom, setShippingFrom] = useState('');

  // Banner panning (background-position)
  const [bgPos, setBgPos] = useState({ x: 50, y: 50 }); // center by default
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const bannerOffsetStartRef = useRef({ delta_x: 0, delta_y: 0 });
  const [bannerOffset, setBannerOffset] = useState({ delta_x: 0, delta_y: 0 });

  // Profile image panning
  const [profileUrl, setProfileUrl] = useState(null);
  const [profileBgPos, setProfileBgPos] = useState({ x: 50, y: 50 }); // center
  const profileIsDraggingRef = useRef(false);
  const profileDragStartRef = useRef({ x: 0, y: 0 });
  const profileOffsetStartRef = useRef({ delta_x: 0, delta_y: 0 });
  const [profileOffset, setProfileOffset] = useState({
    delta_x: 0,
    delta_y: 0,
  });

  const [shopDescription, setShopDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [descriptionSaved, setDescriptionSaved] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  // contact email used in the storefront
  const [contactEmail, setContactEmail] = useState(email || '');

  const [offeredProducts, setOfferedProducts] = useState({
    sign: false,
    keychain: false,
    coaster: false,
    lightbox: false,
    desk_organizer: false,
    bookmark: false,
    cake_topper: false,
  });

  const [platformLinks, setPlatformLinks] = useState({
    shopify: '',
    etsy: '',
    ebay: '',
    other: '',

    // social & extra socials
    instagram: '',
    tiktok: '',
    x: '',
    facebook: '',
    pinterest: '',
    youtube: '',
  });

  const [platformStatus, setPlatformStatus] = useState({
    shopify: 'idle',
    etsy: 'idle',
    ebay: 'idle',
    other: 'idle',

    instagram: 'idle',
    tiktok: 'idle',
    x: 'idle',
    facebook: 'idle',
    pinterest: 'idle',
    youtube: 'idle',
  });

  // which extra socials (from "Other") are enabled
  const [extraSocial, setExtraSocial] = useState({
    facebook: false,
    pinterest: false,
    youtube: false,
  });

  // current selection in the "Other" dropdown
  const [selectedOtherSocial, setSelectedOtherSocial] = useState('facebook');

  // helper lists & labels for the "Other" dropdown
  const allOtherSocialKeys = ['facebook', 'pinterest', 'youtube'];

  const otherSocialLabels = {
    facebook: 'Facebook',
    pinterest: 'Pinterest',
    youtube: 'YouTube',
  };

  // which options are still available (not yet added)
  const availableOtherOptions = allOtherSocialKeys.filter(
    (key) => !extraSocial[key]
  );

  // effective selected value (keeps dropdown valid if options change)
  const effectiveSelectedOther =
    availableOtherOptions.includes(selectedOtherSocial) && selectedOtherSocial
      ? selectedOtherSocial
      : availableOtherOptions[0] || '';

  // Validation / highlight states for enabling the shop
  const [profileError, setProfileError] = useState(false);
  const [shippingError, setShippingError] = useState(false);
  const [productsError, setProductsError] = useState(false);
  const [platformsError, setPlatformsError] = useState(false);

  // Floating error bubble (left side)
  const [showActivateBubble, setShowActivateBubble] = useState(false);
  const [activateBubbleItems, setActivateBubbleItems] = useState([]);

  const fileInputRef = useRef(null);
  const profileFileInputRef = useRef(null);
  const username = (email || '').split('@')[0];

  const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

  // Normalize arbitrary user input into a URL-safe shop slug
  const normalizeShopSlug = (raw) => {
    if (!raw) return '';

    let s = raw.trim().toLowerCase();

    // Remove accents/diacritics (é → e, etc.)
    try {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch {
      // normalize might not exist in some environments; ignore
    }

    // Replace any sequence of non a-z0-9 with a single dash
    s = s.replace(/[^a-z0-9]+/g, '-');

    // Trim leading/trailing dashes
    s = s.replace(/^-+|-+$/g, '');

    return s;
  };

  // --- Simple responsive flag ---
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helpers: selling platforms ONLY (NOT socials)
  const sellingPlatformKeys = ['shopify', 'etsy', 'ebay', 'other'];

  const hasAnySellingPlatform = (linksObj) =>
    sellingPlatformKeys.some((k) => (linksObj?.[k] || '').trim().length > 0);

  const getMissingActivationItems = () => {
    const missing = [];

    const profileOk = !!profileUrl;
    const shippingOk = (shippingFrom || '').trim().length > 0;
    const productsOk = Object.values(offeredProducts).some(Boolean);
    const platformsOk = hasAnySellingPlatform(platformLinks); // ✅ only selling platforms

    if (!profileOk) missing.push('Profile image');
    if (!shippingOk) missing.push('"Shipping from"');
    if (!productsOk) missing.push('At least 1 featured product');
    if (!platformsOk) missing.push('At least 1 selling platform link (Shopify/Etsy/eBay/Other)');

    return {
      missing,
      profileOk,
      shippingOk,
      productsOk,
      platformsOk,
      allOk: profileOk && shippingOk && productsOk && platformsOk,
    };
  };

  const postShopActive = async (activeValue) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('active', String(activeValue));

      const r = await fetch('/shop-info/activate/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        console.warn('Failed to update active_shop on server');
      }
    } catch (err) {
      console.warn('Error calling /shop-info/activate/:', err);
    }
  };

  // ✅ If shop is active and becomes invalid, auto-deactivate + show bubble
  useEffect(() => {
    if (!email) return;

    if (shopEnabled) {
      const { allOk, missing } = getMissingActivationItems();
      if (!allOk) {
        setShowActivateBubble(true);
        setActivateBubbleItems(missing);

        // Auto turn off locally + server-side
        setShopEnabled(false);
        postShopActive(false);

        // Also highlight missing sections
        const { profileOk, shippingOk, productsOk, platformsOk } =
          getMissingActivationItems();
        setProfileError(!profileOk);
        setShippingError(!shippingOk);
        setProductsError(!productsOk);
        setPlatformsError(!platformsOk);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopEnabled, profileUrl, shippingFrom, offeredProducts, platformLinks, email]);

  // --- Load shop_info.json + detect existing banner/profile images ---
  useEffect(() => {
    if (!email) return;

    const fetchInfo = async () => {
      try {
        const r = await fetch(
          `/shop-info/?username=${encodeURIComponent(
            username
          )}&email=${encodeURIComponent(email)}`,
          { cache: 'no-store' }
        );
        if (!r.ok) throw new Error('Failed to fetch /shop-info');

        const data = await r.json();
        setShopEnabled(Boolean(data.active_shop));
        setShopName(data.shop_name || username);

        // Contact email from JSON or fallback to full user email
        let storedContactEmail = '';

        if (
          typeof data.contact_email === 'string' &&
          data.contact_email.trim().length > 0
        ) {
          storedContactEmail = data.contact_email.trim();
        } else {
          storedContactEmail = email || '';
        }

        // If backend only stored the username as contact_email, but we know the full email,
        // prefer the full email in the UI.
        if (storedContactEmail === username && email) {
          storedContactEmail = email;
          const storedShippingFromInner =
            typeof data.shipping_from === 'string' ? data.shipping_from : '';
          setShippingFrom(storedShippingFromInner);
        }

        setContactEmail(storedContactEmail);

        // ✅ Load description from JSON (description or shop_description)
        const desc =
          typeof data.description === 'string'
            ? data.description
            : typeof data.shop_description === 'string'
            ? data.shop_description
            : '';
        setShopDescription(desc || '');
        setDescriptionSaved(false);
        setDescriptionError(false);

        // Offsets
        const storedBannerOffset =
          data.banner_offset && typeof data.banner_offset === 'object'
            ? data.banner_offset
            : {};
        const storedProfileOffset =
          data.profile_offset && typeof data.profile_offset === 'object'
            ? data.profile_offset
            : {};

        const banner_dx = Number.isFinite(storedBannerOffset.delta_x)
          ? storedBannerOffset.delta_x
          : 0;
        const banner_dy = Number.isFinite(storedBannerOffset.delta_y)
          ? storedBannerOffset.delta_y
          : 0;

        const profile_dx = Number.isFinite(storedProfileOffset.delta_x)
          ? storedProfileOffset.delta_x
          : 0;
        const profile_dy = Number.isFinite(storedProfileOffset.delta_y)
          ? storedProfileOffset.delta_y
          : 0;

        setBannerOffset({ delta_x: banner_dx, delta_y: banner_dy });
        setProfileOffset({ delta_x: profile_dx, delta_y: profile_dy });

        const bannerX = clamp(50 - banner_dx / 5, 0, 100);
        const bannerY = clamp(50 - banner_dy / 5, 0, 100);
        setBgPos({ x: bannerX, y: bannerY });

        const profileX = clamp(50 - profile_dx / 5, 0, 100);
        const profileY = clamp(50 - profile_dy / 5, 0, 100);
        setProfileBgPos({ x: profileX, y: profileY });

        // Offered products (backwards compatible: default false)
        const op =
          data.offered_products && typeof data.offered_products === 'object'
            ? data.offered_products
            : {};

        setOfferedProducts({
          sign: !!op.sign,
          keychain: !!op.keychain,
          coaster: !!op.coaster,
          lightbox: !!op.lightbox,
          desk_organizer: !!op.desk_organizer,
          bookmark: !!op.bookmark,
          cake_topper: !!op.cake_topper,
        });

        // Shipping from: empty by default if not set
        const storedShippingFrom =
          typeof data.shipping_from === 'string' ? data.shipping_from : '';
        setShippingFrom(storedShippingFrom);

        // Selling platforms (Shopify / Etsy / eBay / Other)
        const sp =
          data.selling_platforms && typeof data.selling_platforms === 'object'
            ? data.selling_platforms
            : {};

        // Social media links (Instagram, TikTok, X, Facebook, Pinterest, YouTube)
        const sm =
          data.social_media && typeof data.social_media === 'object'
            ? data.social_media
            : {};

        setPlatformLinks({
          shopify: sp.shopify || '',
          etsy: sp.etsy || '',
          ebay: sp.ebay || '',
          other: sp.other || '',

          instagram: sm.instagram || '',
          tiktok: sm.tiktok || '',
          x: sm.x || '',
          facebook: sm.facebook || '',
          pinterest: sm.pinterest || '',
          youtube: sm.youtube || '',
        });

        // ✅ Auto-enable extra socials that have saved links
        setExtraSocial({
          facebook:
            typeof sm.facebook === 'string' && sm.facebook.trim().length > 0,
          pinterest:
            typeof sm.pinterest === 'string' && sm.pinterest.trim().length > 0,
          youtube:
            typeof sm.youtube === 'string' && sm.youtube.trim().length > 0,
        });

        // Clear previous errors on fresh load
        setProfileError(false);
        setShippingError(false);
        setProductsError(false);
        setPlatformsError(false);
        setShowActivateBubble(false);
        setActivateBubbleItems([]);
      } catch (err) {
        console.warn('Failed to fetch shop info:', err);
      }

      // Banner file presence
      try {
        const headBanner = await fetch(`/outputs/${username}/Shop/Banner.png`, {
          method: 'HEAD',
          cache: 'no-store',
        });
        if (headBanner.ok) {
          setHasBanner(true);
          setBannerUrl(`/outputs/${username}/Shop/Banner.png?t=${Date.now()}`);
        } else {
          setHasBanner(false);
          setBannerUrl(null);
        }
      } catch {
        setHasBanner(false);
        setBannerUrl(null);
      }

      // Profile file presence
      try {
        const headProfile = await fetch(`/outputs/${username}/Shop/Profile.png`, {
          method: 'HEAD',
          cache: 'no-store',
        });
        if (headProfile.ok) {
          setProfileUrl(`/outputs/${username}/Shop/Profile.png?t=${Date.now()}`);
        } else {
          setProfileUrl(null);
        }
      } catch (e) {
        console.warn('No profile image found (or HEAD failed):', e);
        setProfileUrl(null);
      }
    };

    fetchInfo();
  }, [email, username]);

  const handleSaveDescription = async () => {
    if (!email) return;

    setIsSavingDescription(true);
    setDescriptionSaved(false);
    setDescriptionError(false);

    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('description', shopDescription);

      const resp = await fetch('/shop-info/set-description/', {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) {
        throw new Error('Non-200 response');
      }

      setDescriptionSaved(true);
    } catch (err) {
      console.error('Failed to save description', err);
      setDescriptionError(true);
    } finally {
      setIsSavingDescription(false);
    }
  };

  // ---- Save "Shipping from" ----
  const handleSaveShippingFrom = async () => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('shipping_from', shippingFrom || '');

      const r = await fetch('/shop-info/set-shipping/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Failed to save shipping location.');
        return;
      }

      alert('"Shipping from" updated!');
    } catch (err) {
      console.error('Error saving "Shipping from":', err);
      alert('Error saving shipping location.');
    }
  };

  // ---- Upload Banner ----
  const handleBannerUpload = async (e) => {
    const inputEl = e.target;
    const file = inputEl.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('banner', file);
    formData.append('username', username);

    try {
      const r = await fetch('/upload-shop-banner/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Upload failed.');
        return;
      }

      const data = await r.json();
      const url = data.url || `/outputs/${username}/Shop/Banner.png`;
      setBannerUrl(url + '?t=' + Date.now());
      setHasBanner(true);
      setBannerOffset({ delta_x: 0, delta_y: 0 });
      setBgPos({ x: 50, y: 50 });
    } catch (err) {
      console.error('Banner upload failed:', err);
      alert('Upload failed.');
    } finally {
      if (inputEl) inputEl.value = '';
    }
  };

  // ---- Delete Banner ----
  const handleDeleteBanner = async () => {
    try {
      const formData = new FormData();
      formData.append('username', username);

      const r = await fetch('/delete-shop-banner/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Failed to delete banner.');
        return;
      }

      setBannerUrl(null);
      setHasBanner(false);
      setBannerOffset({ delta_x: 0, delta_y: 0 });
      setBgPos({ x: 50, y: 50 });

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Delete banner failed:', err);
      alert('Error deleting banner.');
    }
  };

  // ---- Upload Profile Image ----
  const handleProfileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('profile', file);
    formData.append('username', username);

    try {
      const r = await fetch('/upload-shop-profile/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Profile upload failed.');
        return;
      }

      const data = await r.json();
      const url = data.url || `/outputs/${username}/Shop/Profile.png`;
      setProfileUrl(url + '?t=' + Date.now());
      setProfileOffset({ delta_x: 0, delta_y: 0 });
      setProfileBgPos({ x: 50, y: 50 });
      setProfileError(false);
    } catch (err) {
      console.error('Profile upload failed:', err);
      alert('Profile upload failed.');
    }
  };

  // ---- Delete Profile Image ----
  const handleDeleteProfile = async () => {
    try {
      const formData = new FormData();
      formData.append('username', username);

      const r = await fetch('/delete-shop-profile/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Failed to delete profile image.');
        return;
      }

      setProfileUrl(null);
      setProfileOffset({ delta_x: 0, delta_y: 0 });
      setProfileBgPos({ x: 50, y: 50 });
    } catch (err) {
      console.error('Delete profile failed:', err);
      alert('Error deleting profile image.');
    }
  };

  // ---- Save Banner + Profile Offsets ----
  const handleSaveOffset = async () => {
    try {
      const formData = new FormData();
      formData.append('username', username);

      formData.append('banner_delta_x', String(bannerOffset.delta_x));
      formData.append('banner_delta_y', String(bannerOffset.delta_y));
      formData.append('profile_delta_x', String(profileOffset.delta_x));
      formData.append('profile_delta_y', String(profileOffset.delta_y));

      const r = await fetch('/shop-info/set-offset/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Failed to save positions.');
        return;
      }

      alert('Positions saved!');
    } catch (err) {
      console.error('Error saving offsets:', err);
      alert('Error saving positions.');
    }
  };

  // ---- Toggle Activate Shop ----
  const handleToggleShop = async () => {
    const next = !shopEnabled;

    // Clear bubble on any toggle attempt
    setShowActivateBubble(false);
    setActivateBubbleItems([]);

    if (next) {
      const { allOk, missing, profileOk, shippingOk, productsOk, platformsOk } =
        getMissingActivationItems();

      setProfileError(!profileOk);
      setShippingError(!shippingOk);
      setProductsError(!productsOk);
      setPlatformsError(!platformsOk);

      if (!allOk) {
        setShowActivateBubble(true);
        setActivateBubbleItems(missing);
        return;
      }
    }

    setShopEnabled(next);
    await postShopActive(next);
  };

  // ---- Save Shop Name + Contact Email ----
  const handleSaveShopName = async () => {
    try {
      const cleanedShopName = normalizeShopSlug(shopName) || username;

      if (cleanedShopName !== shopName) {
        setShopName(cleanedShopName);
      }

      const formData = new FormData();
      formData.append('username', username);
      formData.append('shop_name', cleanedShopName);
      formData.append('contact_email', contactEmail || '');

      const r = await fetch('/shop-info/set-name/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        alert('Failed to save shop name.');
        return;
      }

      alert('Shop name updated!');
    } catch (err) {
      console.error('Error saving shop name:', err);
      alert('Error saving shop name.');
    }
  };

  // ---- Save + validate platform link ----
  const handleSavePlatformLink = async (key) => {
    let url = (platformLinks[key] || '').trim();

    if (!url) {
      setPlatformStatus((prev) => ({ ...prev, [key]: 'error' }));
      return;
    }

    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
      setPlatformLinks((prev) => ({ ...prev, [key]: url }));
    }

    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch {
      setPlatformStatus((prev) => ({ ...prev, [key]: 'error' }));
      return;
    }

    setPlatformStatus((prev) => ({ ...prev, [key]: 'loading' }));

    const endpoint = '/shop-info/set-platform/';

    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('platform', key);
      formData.append('url', url);

      const resp = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) {
        console.warn('Backend save failed for platform:', key);
        setPlatformStatus((prev) => ({ ...prev, [key]: 'error' }));
        return;
      }

      let ok = true;
      try {
        const data = await resp.json();
        if (typeof data.ok === 'boolean') ok = data.ok;
      } catch {
        // ignore JSON errors
      }

      setPlatformStatus((prev) => ({
        ...prev,
        [key]: ok ? 'success' : 'error',
      }));

      // ✅ Only selling platforms clear the activation lock condition
      if (ok && sellingPlatformKeys.includes(key)) {
        const linksAfter = { ...platformLinks, [key]: url };
        if (hasAnySellingPlatform(linksAfter)) setPlatformsError(false);
      }
    } catch (err) {
      console.warn('Platform link save/check failed:', err);
      setPlatformStatus((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  const handleDeletePlatformLink = async (key) => {
    setPlatformLinks((prev) => ({ ...prev, [key]: '' }));
    setPlatformStatus((prev) => ({ ...prev, [key]: 'idle' }));

    const endpoint = '/shop-info/set-platform/';

    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('platform', key);
      formData.append('url', '');

      const resp = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) {
        console.warn('Backend delete/clear failed for platform:', key);
        setPlatformStatus((prev) => ({ ...prev, [key]: 'error' }));
      }
    } catch (err) {
      console.warn('Error clearing platform link:', err);
      setPlatformStatus((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  // ---- Banner drag logic ----
  const handleBannerMouseDown = (e) => {
    if (!bannerUrl) return;
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    bannerOffsetStartRef.current = { ...bannerOffset };
  };

  const handleBannerMouseMove = (e) => {
    if (!isDraggingRef.current) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    const newDeltaX = bannerOffsetStartRef.current.delta_x + dx;
    const newDeltaY = bannerOffsetStartRef.current.delta_y + dy;

    setBannerOffset({ delta_x: newDeltaX, delta_y: newDeltaY });

    const newX = clamp(50 - newDeltaX / 5, 0, 100);
    const newY = clamp(50 - newDeltaY / 5, 0, 100);
    setBgPos({ x: newX, y: newY });
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
  };

  // ---- Profile drag logic ----
  const handleProfileMouseDown = (e) => {
    if (!profileUrl) return;
    e.stopPropagation();
    profileIsDraggingRef.current = true;
    profileDragStartRef.current = { x: e.clientX, y: e.clientY };
    profileOffsetStartRef.current = { ...profileOffset };
  };

  const handleProfileMouseMove = (e) => {
    if (!profileIsDraggingRef.current) return;
    e.stopPropagation();

    const dx = e.clientX - profileDragStartRef.current.x;
    const dy = e.clientY - profileDragStartRef.current.y;

    const newDeltaX = profileOffsetStartRef.current.delta_x + dx;
    const newDeltaY = profileOffsetStartRef.current.delta_y + dy;

    setProfileOffset({ delta_x: newDeltaX, delta_y: newDeltaY });

    const newX = clamp(50 - newDeltaX / 5, 0, 100);
    const newY = clamp(50 - newDeltaY / 5, 0, 100);
    setProfileBgPos({ x: newX, y: newY });
  };

  const stopProfileDragging = (e) => {
    if (e) e.stopPropagation();
    profileIsDraggingRef.current = false;
  };

  // ---- Offered products checkbox change ----
  const toggleOfferedProduct = (key) => {
    setOfferedProducts((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (Object.values(next).some(Boolean)) {
        setProductsError(false);
      }
      return next;
    });
  };

  // --- Derived helpers for styles ---
  const shippingInputStyle = {
    fontSize: '2vh',
    padding: '4px 8px',
    borderRadius: '6px',
    border: shippingError ? '2px solid #f97373' : '1px solid #cbd5e1',
    backgroundColor: shippingError ? '#fee2e2' : 'white',
    minWidth: isMobile ? '100%' : '240px',
    width: isMobile ? '100%' : 'auto',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const profileCircleBorderBase = {
    width: '100%',
    height: '100%',
    border: profileUrl ? '2px solid #bbb' : '2px dashed #bbb',
    borderRadius: '50%',
    cursor: profileUrl ? 'grab' : 'pointer',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
    backgroundImage: profileUrl ? `url(${profileUrl})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: profileUrl
      ? `${profileBgPos.x}% ${profileBgPos.y}%`
      : 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    fontSize: '1.6vh',
    userSelect: 'none',
    position: 'relative',
    boxShadow: '0 0 10px rgba(0,0,0,0.3)',
    transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
  };

  if (profileError) {
    profileCircleBorderBase.border = '2px solid #f97373';
    profileCircleBorderBase.boxShadow = '0 0 0 3px rgba(248,113,113,0.7)';
    profileCircleBorderBase.backgroundColor = '#fee2e2';
  }

  const offeredProductsBoxStyle = {
    marginTop: '16px',
    textAlign: 'center',
    padding: '10px',
    borderRadius: '8px',
    border: productsError ? '2px solid #f97373' : 'none',
    backgroundColor: productsError ? '#fee2e2' : 'transparent',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const platformsSectionStyle = {
    marginTop: '24px',
    textAlign: 'center',
    padding: '10px',
    borderRadius: '8px',
    border: platformsError ? '2px solid #f97373' : 'none',
    backgroundColor: platformsError ? '#fee2e2' : 'transparent',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  // ---- Save Offered Products ----
  const handleSaveOfferedProducts = async () => {
    try {
      const formData = new FormData();

      // REQUIRED
      formData.append('username', username);

      formData.append('sign', String(offeredProducts.sign));
      formData.append('keychain', String(offeredProducts.keychain));
      formData.append('coaster', String(offeredProducts.coaster));
      formData.append('lightbox', String(offeredProducts.lightbox));
      formData.append('desk_organizer', String(offeredProducts.desk_organizer));
      formData.append('bookmark', String(offeredProducts.bookmark));
      formData.append('cake_topper', String(offeredProducts.cake_topper));

      const r = await fetch('/shop-info/set-products/', {
        method: 'POST',
        body: formData,
      });

      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        console.error('set-products failed:', r.status, txt);
        alert('Failed to save offered products.');
        return;
      }

      const data = await r.json().catch(() => null);
      console.log('Saved offered_products:', data?.offered_products);

      alert('Offered products updated!');
    } catch (err) {
      console.error('Error saving offered products:', err);
      alert('Error saving offered products.');
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: isMobile ? '10px' : '20px',
        boxSizing: 'border-box',
      }}
    >
      {/* Floating activation bubble */}
      {showActivateBubble && activateBubbleItems.length > 0 && (
        <div
          style={{
            position: 'fixed',
            left: '16px',
            top: '30%',
            transform: 'translateY(-50%)',
            backgroundColor: '#b92c2c',
            color: 'white',
            padding: '14px 16px',
            borderRadius: '10px',
            boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
            zIndex: 9999,
            width: isMobile ? '80vw' : '280px',
            maxWidth: '320px',
            textAlign: 'left',
          }}
        >
          <div style={{ fontWeight: 800, fontSize: '16px', marginBottom: '8px' }}>
            Missing required fields:
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.35 }}>
            {activateBubbleItems.map((item) => (
              <li key={item} style={{ fontSize: '14px' }}>
                {item}
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => {
              setShowActivateBubble(false);
              setActivateBubbleItems([]);
            }}
            style={{
              marginTop: '10px',
              width: '100%',
              padding: '8px 10px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.35)',
              backgroundColor: 'rgba(255,255,255,0.12)',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            OK
          </button>
        </div>
      )}

      {/* --- Header Row --- */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '14px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        {/* Activate toggle + label */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span style={{ fontSize: '2.2vh', fontWeight: 600 }}>
            Activate Shop:
          </span>

          {/* Toggle */}
          <label
            style={{
              position: 'relative',
              display: 'inline-block',
              width: '55px',
              height: '28px',
            }}
          >
            <input
              type="checkbox"
              checked={shopEnabled}
              onChange={handleToggleShop}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: shopEnabled ? '#4ade80' : '#ccc',
                transition: '.3s',
                borderRadius: '34px',
              }}
            />
            <span
              style={{
                position: 'absolute',
                height: '22px',
                width: '22px',
                left: shopEnabled ? '29px' : '4px',
                bottom: '3px',
                backgroundColor: 'white',
                transition: '.3s',
                borderRadius: '50%',
              }}
            />
          </label>
        </div>

        {/* Shop Name controls */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: '4px',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <span style={{ fontSize: '2.1vh', fontWeight: 600 }}>Shop Name:</span>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '6px',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <span
              style={{
                fontSize: '2vh',
                whiteSpace: 'nowrap',
              }}
            >
              https://wabu.club/shop/
            </span>

            <input
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="your-shop"
              style={{
                fontSize: '2vh',
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                width: isMobile ? '40%' : '160px',
                minWidth: isMobile ? '40%' : '140px',
                boxSizing: 'border-box',
              }}
            />

            {/* Copy button */}
            <button
              onClick={() => {
                const fullUrl = `https://wabu.club/shop/${shopName || ''}`;

                try {
                  if (
                    typeof navigator !== 'undefined' &&
                    navigator.clipboard &&
                    typeof navigator.clipboard.writeText === 'function'
                  ) {
                    navigator.clipboard.writeText(fullUrl).catch(() => {
                      const tempInput = document.createElement('input');
                      tempInput.value = fullUrl;
                      document.body.appendChild(tempInput);
                      tempInput.select();
                      document.execCommand('copy');
                      document.body.removeChild(tempInput);
                    });
                  } else {
                    const tempInput = document.createElement('input');
                    tempInput.value = fullUrl;
                    document.body.appendChild(tempInput);
                    tempInput.select();
                    document.execCommand('copy');
                    document.body.removeChild(tempInput);
                  }
                } catch (err) {
                  console.warn('Copy failed:', err);
                  alert(fullUrl);
                }
              }}
              style={{
                padding: '4px 8px',
                fontSize: '2.2vh',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer',
              }}
              title="Copy shop URL"
            >
              📋
            </button>
          </div>
        </div>

        {/* Contact Email controls */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: '4px',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <span style={{ fontSize: '2.1vh', fontWeight: 600 }}>
            Contact Email:
          </span>

          <input
            type="text"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="your@email"
            style={{
              fontSize: '2vh',
              padding: '4px 8px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              minWidth: isMobile ? '100%' : '240px',
              width: isMobile ? '100%' : '240px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Save button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-start' : 'center',
          }}
        >
          <button
            onClick={handleSaveShopName}
            style={{
              padding: '6px 12px',
              fontSize: '1.8vh',
              borderRadius: '6px',
              border: '1px solid #3b82f6',
              backgroundColor: '#3b82f6',
              color: 'white',
              cursor: 'pointer',
              marginLeft: isMobile ? 0 : '10px',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            Save
          </button>
        </div>
      </div>

      {/* --- Bounding Box --- */}
      <div
        style={{
          border: '2px solid #ddd',
          borderRadius: '10px',
          padding: isMobile ? '12px' : '20px',
          minHeight: isMobile ? 'auto' : '60vh',
          position: 'relative',
          backgroundColor: '#fff',
          pointerEvents: 'auto',
          opacity: 1,
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Hidden banner file input */}
        <input
          type="file"
          accept="image/png, image/jpeg"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleBannerUpload}
        />

        {/* Hidden profile file input */}
        <input
          type="file"
          accept="image/png, image/jpeg"
          ref={profileFileInputRef}
          style={{ display: 'none' }}
          onChange={handleProfileUpload}
        />

        {/* Banner Upload / Display Box */}
        <div
          onClick={() => {
            if (!bannerUrl) {
              fileInputRef.current?.click();
            }
          }}
          onMouseDown={handleBannerMouseDown}
          onMouseMove={handleBannerMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
          style={{
            width: '100%',
            aspectRatio: '4 / 1',
            border: bannerUrl ? '2px solid #bbb' : '2px dashed #bbb',
            borderRadius: '12px',
            margin: '0 auto 8vmin auto',
            cursor: bannerUrl ? 'grab' : 'pointer',
            overflow: 'visible',
            backgroundColor: '#fafafa',
            backgroundImage: bannerUrl ? `url(${bannerUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: bannerUrl
              ? `${bgPos.x}% ${bgPos.y}%`
              : 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '1.8vh',
            userSelect: 'none',
            position: 'relative',
          }}
        >
          {!bannerUrl && (
            <div
              style={{
                position: 'absolute',
                top: '8px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '1.8vh',
                color: '#999',
                pointerEvents: 'none',
                userSelect: 'none',
                textAlign: 'center',
                width: '100%',
              }}
            >
              Click to upload your shop banner (recommended 4:1 ratio)
            </div>
          )}

          {/* Banner overlay icons */}
          {bannerUrl && (
            <>
              {/* Save Icon – circular badge */}
              <div
                onMouseDown={(e) => {
                  handleBannerMouseDown(e);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveOffset();
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  backgroundImage: 'url("/icons/save.svg")',
                  backgroundSize: '60%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 0 6px rgba(0,0,0,0.4)',
                  transition: 'opacity 0.2s',
                  opacity: 0.85,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.85)}
              />

              {/* Trash Icon – circular badge */}
              <div
                onMouseDown={(e) => {
                  handleBannerMouseDown(e);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteBanner();
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  backgroundImage: 'url("/icons/trash.svg")',
                  backgroundSize: '60%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 0 6px rgba(0,0,0,0.4)',
                  transition: 'opacity 0.2s',
                  opacity: 0.85,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.85)}
              />
            </>
          )}

          {/* Profile Image Box Overlay (wrapper outside circular frame) */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translate(-50%, -70%)',
              width: '25vmin',
              aspectRatio: '1 / 1',
              pointerEvents: 'auto',
              zIndex: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!profileUrl) {
                profileFileInputRef.current?.click();
              }
            }}
          >
            {/* Inner circular image area (clipping only the image) */}
            <div
              onMouseDown={handleProfileMouseDown}
              onMouseMove={handleProfileMouseMove}
              onMouseUp={stopProfileDragging}
              onMouseLeave={stopProfileDragging}
              style={profileCircleBorderBase}
            >
              {!profileUrl && (
                <span>Click to upload profile image (1:1)</span>
              )}
            </div>

            {profileUrl && (
              <>
                {/* Save Icon – outside the circle, top-right */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveOffset();
                  }}
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    width: '26px',
                    height: '26px',
                    borderRadius: '100%',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    backgroundImage: 'url("/icons/save.svg")',
                    backgroundSize: '90%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    cursor: 'pointer',
                    boxShadow: '0 0 6px rgba(0,0,0,0.4)',
                  }}
                />

                {/* Trash Icon – outside the circle, bottom-left */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProfile();
                  }}
                  style={{
                    position: 'absolute',
                    top: '-5px',
                    left: '-5px',
                    width: '26px',
                    height: '26px',
                    borderRadius: '100%',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    backgroundImage: 'url("/icons/trash.svg")',
                    backgroundSize: '90%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    cursor: 'pointer',
                    boxShadow: '0 0 6px rgba(0,0,0,0.4)',
                  }}
                />
              </>
            )}
          </div>
        </div>

        {/* Shop name display – centered and bold under the banner */}
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: isMobile ? '3.5vh' : '5vh',
            marginTop: '4px',
            wordBreak: 'break-word',
          }}
        >
          {shopName}
        </div>

        {/* "Shipping from" row under shop title */}
        <div
          style={{
            marginTop: '8px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '2.1vh' }}>
            <span style={{ fontWeight: 600 }}>Shipping from:</span>
          </span>

          <input
            type="text"
            value={shippingFrom}
            onChange={(e) => {
              setShippingFrom(e.target.value);
              if (e.target.value.trim().length > 0) {
                setShippingError(false);
              }
            }}
            style={shippingInputStyle}
            placeholder=""
          />

          <button
            onClick={handleSaveShippingFrom}
            style={{
              padding: '6px 12px',
              fontSize: '1.8vh',
              borderRadius: '6px',
              border: '1px solid #3b82f6',
              backgroundColor: '#3b82f6',
              color: 'white',
              cursor: 'pointer',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            Save
          </button>
        </div>

        {/* Shop Description */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '2.5vh',
            marginBottom: '2.5vh',
          }}
        >
          <label
            style={{
              fontWeight: 'bold',
              fontSize: '2.2vmin',
              marginBottom: '1vh',
              textAlign: 'center',
              width: '90%',
            }}
          >
            Shop Description:
          </label>

          <textarea
            value={shopDescription}
            onChange={(e) => setShopDescription(e.target.value)}
            placeholder="Describe your shop, pricing, customization tips, policies, and more..."
            style={{
              width: '90%',
              minHeight: '20vh',
              resize: 'vertical',
              padding: '1.5vmin',
              fontSize: '2vmin',
              borderRadius: '8px',
              border: '1px solid #bbb',
              backgroundColor: '#fafafa',
              boxSizing: 'border-box',
            }}
          />

          <button
            onClick={handleSaveDescription}
            disabled={isSavingDescription}
            style={{
              marginTop: '1.5vh',
              padding: '1vmin 3vmin',
              fontSize: '2vmin',
              fontWeight: 'bold',
              borderRadius: '6px',
              border: 'none',
              cursor: isSavingDescription ? 'default' : 'pointer',
              backgroundColor: isSavingDescription ? '#9ca3af' : '#10b981',
              color: 'white',
            }}
          >
            {isSavingDescription ? 'Saving…' : 'Save Description'}
          </button>

          {descriptionSaved && (
            <p
              style={{
                marginTop: '1vh',
                fontSize: '1.8vmin',
                color: '#16a34a',
                textAlign: 'center',
              }}
            >
              Description saved!
            </p>
          )}

          {descriptionError && (
            <p
              style={{
                marginTop: '1vh',
                fontSize: '1.8vmin',
                color: '#dc2626',
                textAlign: 'center',
              }}
            >
              Failed to save description. Please try again.
            </p>
          )}
        </div>

        {/* Offered products section */}
        <div style={offeredProductsBoxStyle}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '2.4vh',
              marginBottom: '8px',
            }}
          >
            Featured Products:
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              fontSize: '2vh',
            }}
          >
            {[
              ['sign', 'Signs'],
              ['keychain', 'Keychains'],
              ['coaster', 'Coasters'],
              ['lightbox', 'Lightboxes'],
              ['desk_organizer', 'Desk Organizer'],
              ['bookmark', 'Bookmark'],
              ['cake_topper', 'Cake Topper'],
            ].map(([k, label]) => (
              <label key={k} style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={offeredProducts[k]}
                  onChange={() => toggleOfferedProduct(k)}
                  style={{ marginRight: '8px' }}
                />
                {label}
              </label>
            ))}
          </div>

          <button
            onClick={handleSaveOfferedProducts}
            style={{
              marginTop: '10px',
              padding: '6px 16px',
              fontSize: '1.9vh',
              borderRadius: '6px',
              border: '1px solid #10b981',
              backgroundColor: '#10b981',
              color: '#fff',
              cursor: 'pointer',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            Save Offered Products
          </button>
        </div>

        {/* Selling Platforms section */}
        <div style={platformsSectionStyle}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '2.4vh',
              marginBottom: '10px',
            }}
          >
            Selling Platforms:
          </div>

          {/* Rows container */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {[
              { key: 'shopify', label: 'Shopify', icon: '/icons/shopify.svg' },
              { key: 'etsy', label: 'Etsy', icon: '/icons/etsy.svg' },
              { key: 'ebay', label: 'eBay', icon: '/icons/ebay.svg' },
              { key: 'other', label: 'Other', icon: '/icons/other.svg' },
            ].map((p) => (
              <div
                key={p.key}
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: '10px',
                  width: '100%',
                  maxWidth: '600px',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                }}
              >
                {/* Top row: icon + name */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={p.icon}
                      alt={p.label}
                      style={{
                        width: '30px',
                        height: '30px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      minWidth: '80px',
                      textAlign: 'left',
                      fontSize: '2vh',
                      fontWeight: 500,
                    }}
                  >
                    {p.label}
                  </div>
                </div>

                {/* Bottom / right row: link + buttons */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '8px',
                    flexGrow: 1,
                  }}
                >
                  <input
                    type="text"
                    value={platformLinks[p.key]}
                    onChange={(e) =>
                      setPlatformLinks((prev) => {
                        const next = { ...prev, [p.key]: e.target.value };
                        if (hasAnySellingPlatform(next)) setPlatformsError(false);
                        return next;
                      })
                    }
                    placeholder="https://..."
                    style={{
                      flexGrow: 1,
                      fontSize: '1.9vh',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      marginRight: isMobile ? 0 : '8px',
                      width: isMobile ? '100%' : 'auto',
                      boxSizing: 'border-box',
                    }}
                  />

                  <button
                    onClick={() => handleSavePlatformLink(p.key)}
                    style={{
                      padding: '6px 14px',
                      fontSize: '1.8vh',
                      borderRadius: '6px',
                      border: '1px solid #3b82f6',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      cursor: 'pointer',
                      minWidth: isMobile ? '100%' : '60px',
                    }}
                  >
                    {platformStatus[p.key] === 'loading'
                      ? '⏳'
                      : platformStatus[p.key] === 'success'
                      ? '✅'
                      : platformStatus[p.key] === 'error'
                      ? '❌'
                      : 'Save'}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeletePlatformLink(p.key)}
                    style={{
                      padding: isMobile ? '6px 0' : '6px 10px',
                      fontSize: '2vh',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                      cursor: 'pointer',
                      minWidth: isMobile ? '100%' : 'auto',
                    }}
                    title="Clear this platform link"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Social Media section (does NOT block activation) */}
        <div
          style={{
            marginTop: '24px',
            textAlign: 'center',
            padding: '10px',
            borderRadius: '8px',
            border: 'none',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: '2.4vh',
              marginBottom: '10px',
            }}
          >
            Social Media:
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {[
              { key: 'instagram', label: 'Instagram', icon: '/icons/Ig.svg' },
              { key: 'tiktok', label: 'TikTok', icon: '/icons/TikTok.svg' },
              { key: 'x', label: 'X', icon: '/icons/X.svg' },
            ].map((s) => (
              <div
                key={s.key}
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: '10px',
                  width: '100%',
                  maxWidth: '600px',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={s.icon}
                      alt={s.label}
                      style={{
                        width: '30px',
                        height: '30px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      minWidth: '80px',
                      fontSize: '2vh',
                      fontWeight: 500,
                      textAlign: 'left',
                    }}
                  >
                    {s.label}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '8px',
                    flexGrow: 1,
                  }}
                >
                  <input
                    type="text"
                    value={platformLinks[s.key] || ''}
                    onChange={(e) =>
                      setPlatformLinks((prev) => ({
                        ...prev,
                        [s.key]: e.target.value,
                      }))
                    }
                    placeholder="https://..."
                    style={{
                      flexGrow: 1,
                      fontSize: '1.9vh',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      width: isMobile ? '100%' : 'auto',
                      boxSizing: 'border-box',
                    }}
                  />

                  <button
                    onClick={() => handleSavePlatformLink(s.key)}
                    style={{
                      padding: '6px 14px',
                      fontSize: '1.8vh',
                      borderRadius: '6px',
                      border: '1px solid #3b82f6',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      cursor: 'pointer',
                      minWidth: isMobile ? '100%' : '60px',
                    }}
                  >
                    {platformStatus[s.key] === 'loading'
                      ? '⏳'
                      : platformStatus[s.key] === 'success'
                      ? '✅'
                      : platformStatus[s.key] === 'error'
                      ? '❌'
                      : 'Save'}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeletePlatformLink(s.key)}
                    style={{
                      padding: isMobile ? '6px 0' : '6px 10px',
                      fontSize: '2vh',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                      cursor: 'pointer',
                    }}
                    title="Clear this link"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}

            {[
              { key: 'facebook', label: 'Facebook', icon: '/icons/Facebook.svg' },
              { key: 'pinterest', label: 'Pinterest', icon: '/icons/Pinterest.svg' },
              { key: 'youtube', label: 'YouTube', icon: '/icons/Youtube.svg' },
            ]
              .filter((s) => extraSocial[s.key])
              .map((s) => (
                <div
                  key={s.key}
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '10px',
                    width: '100%',
                    maxWidth: '600px',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={s.icon}
                        alt={s.label}
                        style={{
                          width: '30px',
                          height: '30px',
                          objectFit: 'contain',
                        }}
                      />
                    </div>

                    <div
                      style={{
                        minWidth: '80px',
                        fontSize: '2vh',
                        fontWeight: 500,
                        textAlign: 'left',
                      }}
                    >
                      {s.label}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: '8px',
                      flexGrow: 1,
                    }}
                  >
                    <input
                      type="text"
                      value={platformLinks[s.key] || ''}
                      onChange={(e) =>
                        setPlatformLinks((prev) => ({
                          ...prev,
                          [s.key]: e.target.value,
                        }))
                      }
                      placeholder="https://..."
                      style={{
                        flexGrow: 1,
                        fontSize: '1.9vh',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        width: isMobile ? '100%' : 'auto',
                        boxSizing: 'border-box',
                      }}
                    />

                    <button
                      onClick={() => handleSavePlatformLink(s.key)}
                      style={{
                        padding: '6px 14px',
                        fontSize: '1.8vh',
                        borderRadius: '6px',
                        border: '1px solid #3b82f6',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        cursor: 'pointer',
                        minWidth: isMobile ? '100%' : '60px',
                      }}
                    >
                      {platformStatus[s.key] === 'loading'
                        ? '⏳'
                        : platformStatus[s.key] === 'success'
                        ? '✅'
                        : platformStatus[s.key] === 'error'
                        ? '❌'
                        : 'Save'}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleDeletePlatformLink(s.key);
                        setExtraSocial((prev) => ({ ...prev, [s.key]: false }));
                      }}
                      style={{
                        padding: isMobile ? '6px 0' : '6px 10px',
                        fontSize: '2vh',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        cursor: 'pointer',
                      }}
                      title="Clear this link"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}

            {/* "Other" row with dropdown + "+" button */}
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: '10px',
                width: '100%',
                maxWidth: '600px',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '10px',
                  minWidth: '128px',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src="/icons/other.svg"
                    alt="Other"
                    style={{
                      width: '30px',
                      height: '30px',
                      objectFit: 'contain',
                    }}
                  />
                </div>

                <div style={{ fontSize: '2vh', fontWeight: 500 }}>Other</div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: '8px',
                  flexGrow: 1,
                  boxSizing: 'border-box',
                }}
              >
                <select
                  value={effectiveSelectedOther}
                  onChange={(e) => setSelectedOtherSocial(e.target.value)}
                  disabled={availableOtherOptions.length === 0}
                  style={{
                    flexGrow: 1,
                    fontSize: '1.9vh',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    width: isMobile ? '100%' : 'auto',
                    boxSizing: 'border-box',
                  }}
                >
                  {availableOtherOptions.length === 0 ? (
                    <option value="">No more options</option>
                  ) : (
                    availableOtherOptions.map((key) => (
                      <option key={key} value={key}>
                        {otherSocialLabels[key]}
                      </option>
                    ))
                  )}
                </select>

                <button
                  type="button"
                  disabled={
                    availableOtherOptions.length === 0 || !effectiveSelectedOther
                  }
                  onClick={() => {
                    if (!effectiveSelectedOther) return;
                    setExtraSocial((prev) => ({
                      ...prev,
                      [effectiveSelectedOther]: true,
                    }));
                  }}
                  style={{
                    padding: '6px 14px',
                    fontSize: '1.8vh',
                    borderRadius: '6px',
                    border: '1px solid #22c55e',
                    backgroundColor:
                      availableOtherOptions.length === 0 || !effectiveSelectedOther
                        ? '#9ca3af'
                        : '#22c55e',
                    color: 'white',
                    cursor:
                      availableOtherOptions.length === 0 || !effectiveSelectedOther
                        ? 'default'
                        : 'pointer',
                    minWidth: isMobile ? '100%' : '60px',
                  }}
                  title="Add selected social platform"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* (future shop content goes here) */}
      </div>
    </div>
  );
}
